// Orders API - Functions for managing orders in Supabase
const { supabase } = require('../supabase.js')

/**
 * Input sanitization to prevent XSS attacks
 * @param {string} input
 * @returns {string}
 */
function sanitizeInput(input) {
  if (typeof input !== 'string') return input
  return input.replace(/<[^>]*>/g, '').trim()
}

/**
 * Orders API for mattress configurator
 */
const ordersApi = {
  /**
   * Create order from cart data
   * @param {Object} orderData - Order information
   * @param {Object} orderData.customer - Customer details
   * @param {string} orderData.customer.name - Customer name
   * @param {string} orderData.customer.email - Customer email
   * @param {string} [orderData.customer.phone] - Customer phone
   * @param {'pickup'|'courier'} orderData.customer.deliveryMethod - Delivery method
   * @param {'card'|'cash'|'bank'} orderData.customer.paymentMethod - Payment method
   * @param {string} [orderData.customer.address] - Delivery address
   * @param {string} [orderData.customer.city] - Delivery city
   * @param {string} [orderData.customer.postalCode] - Postal code
   * @param {string} [orderData.customer.deliveryNotes] - Delivery notes
   * @param {Array} orderData.items - Cart items
   * @param {number} orderData.total - Total amount
   * @returns {Promise<Order>} Created order
   */
  async createOrder(orderData) {
    try {
      console.log('🔄 Creating new order...')
      
      // Sanitize customer data
      const sanitizedCustomer = {
        name: sanitizeInput(orderData.customer.name),
        email: sanitizeInput(orderData.customer.email),
        phone: sanitizeInput(orderData.customer.phone),
        deliveryMethod: orderData.customer.deliveryMethod,
        paymentMethod: orderData.customer.paymentMethod
      }

      // Prepare delivery address if courier delivery
      let deliveryAddress = null
      if (sanitizedCustomer.deliveryMethod === 'courier') {
        deliveryAddress = {
          address: sanitizeInput(orderData.customer.address),
          city: sanitizeInput(orderData.customer.city),
          postalCode: sanitizeInput(orderData.customer.postalCode),
          deliveryNotes: sanitizeInput(orderData.customer.deliveryNotes)
        }
      }

      // Create order
      const { data: order, error: orderError } = await supabase
        .from('orders')
        .insert({
          customer_name: sanitizedCustomer.name,
          customer_email: sanitizedCustomer.email,
          customer_phone: sanitizedCustomer.phone,
          delivery_method: sanitizedCustomer.deliveryMethod,
          payment_method: sanitizedCustomer.paymentMethod,
          delivery_address: deliveryAddress,
          total_amount: orderData.total
        })
        .select()
        .single()

      if (orderError) {
        console.error('❌ Error creating order:', orderError)
        throw new Error(`Failed to create order: ${orderError.message}`)
      }

      console.log(`✅ Created order: ${order.order_number}`)

      // Create order items
      const orderItems = orderData.items.map(item => ({
        order_id: order.id,
        product_name: sanitizeInput(item.name || `Матрас ${item.configuration?.size || 'Custom'}`),
        quantity: item.quantity || 1,
        unit_price: item.price || 0,
        total_price: (item.price || 0) * (item.quantity || 1),
        configuration: {
          cover: sanitizeInput(item.configuration?.cover || ''),
          size: sanitizeInput(item.configuration?.size || ''),
          height: sanitizeInput(item.configuration?.height || ''),
          layer1: sanitizeInput(item.configuration?.layer1 || ''),
          layer2: sanitizeInput(item.configuration?.layer2 || ''),
          layer3: sanitizeInput(item.configuration?.layer3 || '')
        }
      }))

      const { error: itemsError } = await supabase
        .from('order_items')
        .insert(orderItems)

      if (itemsError) {
        console.error('❌ Error creating order items:', itemsError)
        throw new Error(`Failed to create order items: ${itemsError.message}`)
      }

      console.log(`✅ Created ${orderItems.length} order items`)

      return order
    } catch (error) {
      console.error('💥 Failed to create order:', error)
      throw error
    }
  },

  /**
   * Get order with items by ID
   * @param {string} orderId - Order ID
   * @returns {Promise<Order|null>} Order with items
   */
  async getOrder(orderId) {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          order_items (*)
        `)
        .eq('id', orderId)
        .single()

      if (error && error.code !== 'PGRST116') {
        console.error(`❌ Error loading order ${orderId}:`, error)
        throw new Error(`Failed to load order: ${error.message}`)
      }

      return data
    } catch (error) {
      console.error(`💥 Failed to load order ${orderId}:`, error)
      throw error
    }
  },

  /**
   * Get order by order number
   * @param {string} orderNumber - Order number (e.g., 'ORD-20250118-0001')
   * @returns {Promise<Order|null>} Order with items
   */
  async getOrderByNumber(orderNumber) {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          order_items (*)
        `)
        .eq('order_number', orderNumber)
        .single()

      if (error && error.code !== 'PGRST116') {
        console.error(`❌ Error loading order ${orderNumber}:`, error)
        throw new Error(`Failed to load order: ${error.message}`)
      }

      return data
    } catch (error) {
      console.error(`💥 Failed to load order ${orderNumber}:`, error)
      throw error
    }
  },

  /**
   * Update order status (admin function)
   * @param {string} orderId - Order ID
   * @param {'pending'|'confirmed'|'production'|'ready'|'shipped'|'delivered'|'cancelled'} status - New status
   * @returns {Promise<Order>} Updated order
   */
  async updateOrderStatus(orderId, status) {
    try {
      const { data, error } = await supabase
        .from('orders')
        .update({ status })
        .eq('id', orderId)
        .select()
        .single()

      if (error) {
        console.error(`❌ Error updating order status for ${orderId}:`, error)
        throw new Error(`Failed to update order status: ${error.message}`)
      }

      console.log(`✅ Updated order ${data.order_number} status to: ${status}`)
      return data
    } catch (error) {
      console.error(`💥 Failed to update order status for ${orderId}:`, error)
      throw error
    }
  },

  /**
   * Get orders list (admin function)
   * @param {Object} [filters] - Filter options
   * @param {'pending'|'confirmed'|'production'|'ready'|'shipped'|'delivered'|'cancelled'} [filters.status] - Filter by status
   * @param {number} [filters.limit] - Limit number of results
   * @param {number} [filters.offset] - Offset for pagination
   * @returns {Promise<Order[]>} List of orders
   */
  async getOrders(filters = {}) {
    try {
      let query = supabase
        .from('orders')
        .select(`
          *,
          order_items (*)
        `)
        .order('created_at', { ascending: false })

      if (filters.status) {
        query = query.eq('status', filters.status)
      }

      if (filters.limit) {
        query = query.limit(filters.limit)
      }

      if (filters.offset) {
        query = query.range(filters.offset, filters.offset + (filters.limit || 10) - 1)
      }

      const { data, error } = await query

      if (error) {
        console.error('❌ Error loading orders:', error)
        throw new Error(`Failed to load orders: ${error.message}`)
      }

      return data || []
    } catch (error) {
      console.error('💥 Failed to load orders:', error)
      throw error
    }
  },

  /**
   * Get order statistics (admin function)
   * @returns {Promise<Object>} Order statistics
   */
  async getOrderStats() {
    try {
      // Get total orders count
      const { count: totalOrders, error: totalError } = await supabase
        .from('orders')
        .select('*', { count: 'exact', head: true })

      if (totalError) throw totalError

      // Get orders by status
      const { data: statusCounts, error: statusError } = await supabase
        .from('orders')
        .select('status')

      if (statusError) throw statusError

      // Calculate status breakdown
      const statusBreakdown = statusCounts.reduce((acc, order) => {
        acc[order.status] = (acc[order.status] || 0) + 1
        return acc
      }, {})

      // Get recent orders count (last 7 days)
      const weekAgo = new Date()
      weekAgo.setDate(weekAgo.getDate() - 7)

      const { count: recentOrders, error: recentError } = await supabase
        .from('orders')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', weekAgo.toISOString())

      if (recentError) throw recentError

      return {
        totalOrders,
        recentOrders,
        statusBreakdown
      }
    } catch (error) {
      console.error('💥 Failed to load order statistics:', error)
      throw error
    }
  }
}

module.exports = { ordersApi };